import React, { createContext, useContext, useState, ReactNode } from 'react';

interface WindowData {
  id: string;
  title: string;
  component: React.ComponentType<any>;
  isMinimized: boolean;
  isMaximized: boolean;
  position: { x: number; y: number };
  size: { width: number; height: number };
  zIndex: number;
}

interface WindowContextType {
  windows: WindowData[];
  openWindow: (windowData: Omit<WindowData, 'id' | 'zIndex'>) => void;
  closeWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  maximizeWindow: (id: string) => void;
  restoreWindow: (id: string) => void;
  focusWindow: (id: string) => void;
}

const WindowContext = createContext<WindowContextType | undefined>(undefined);

export const useWindows = () => {
  const context = useContext(WindowContext);
  if (!context) {
    throw new Error('useWindows must be used within a WindowProvider');
  }
  return context;
};

export const WindowProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [windows, setWindows] = useState<WindowData[]>([]);
  const [nextZIndex, setNextZIndex] = useState(100);

  const openWindow = (windowData: Omit<WindowData, 'id' | 'zIndex'>) => {
    const newWindow: WindowData = {
      ...windowData,
      id: Date.now().toString(),
      zIndex: nextZIndex,
    };
    setWindows(prev => [...prev, newWindow]);
    setNextZIndex(prev => prev + 1);
  };

  const closeWindow = (id: string) => {
    setWindows(prev => prev.filter(window => window.id !== id));
  };

  const minimizeWindow = (id: string) => {
    setWindows(prev => 
      prev.map(window => 
        window.id === id ? { ...window, isMinimized: true } : window
      )
    );
  };

  const maximizeWindow = (id: string) => {
    setWindows(prev => 
      prev.map(window => 
        window.id === id ? { ...window, isMaximized: !window.isMaximized } : window
      )
    );
  };

  const restoreWindow = (id: string) => {
    setWindows(prev => 
      prev.map(window => 
        window.id === id ? { ...window, isMinimized: false } : window
      )
    );
    focusWindow(id);
  };

  const focusWindow = (id: string) => {
    setWindows(prev => 
      prev.map(window => 
        window.id === id ? { ...window, zIndex: nextZIndex, isMinimized: false } : window
      )
    );
    setNextZIndex(prev => prev + 1);
  };

  return (
    <WindowContext.Provider value={{
      windows,
      openWindow,
      closeWindow,
      minimizeWindow,
      maximizeWindow,
      restoreWindow,
      focusWindow
    }}>
      {children}
    </WindowContext.Provider>
  );
};
